import { useState, useCallback, useEffect } from "react";

// API-backed engines (compatibility layer)
import { initApiEngines, griffinConfig } from './api-engines.js';
import { api } from './api-client.js';

// Plain engine imports — only for enums/constants/utilities used by modules
import { runQualityGate, extractActivityProfile } from './engines/monitoring-engine.js';

// Auth
import { getUser, logout } from './auth.js';

// Theme
import { T, font } from './theme.js';

// UI Modules
import CRMModule from './modules/CRMModule.jsx';
import KYCModule from './modules/KYCModule.jsx';
import LendingModule from './modules/LendingModule.jsx';
import AccountsModule from './modules/AccountsModule.jsx';
import PaymentsModule from './modules/PaymentsModule.jsx';
import MonitoringModule from './modules/MonitoringModule.jsx';
import ComplianceModule from './modules/ComplianceModule.jsx';
import FinanceModule from './modules/FinanceModule.jsx';
import InvoiceFinanceModule from './modules/InvoiceFinanceModule.jsx';
import DashboardModule from './modules/DashboardModule.jsx';

// ============================================================
// APPLICATION SHELL
// ============================================================

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: '\ud83c\udfe0' },
  { key: 'crm', label: 'CRM', icon: '\ud83d\udcbc' },
  { key: 'customers', label: 'Customers', icon: '\ud83d\udc65' },
  { key: 'accounts', label: 'Accounts', icon: '\ud83d\udcca' },
  { key: 'lending', label: 'Lending', icon: '\ud83c\udfe6' },
  { key: 'payments', label: 'Payments', icon: '\ud83d\udcb8' },
  { key: 'monitoring', label: 'Monitoring', icon: '\ud83d\udd0d' },
  { key: 'compliance', label: 'Compliance', icon: '\ud83d\udccb' },
  { key: 'invoice-finance', label: 'Invoice Finance', icon: '\ud83d\udccf' },
  { key: 'finance', label: 'Finance', icon: '\ud83d\udcc8' },
];

export default function CoreBankApp() {
  const [engines, setEngines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [moduleContext, setModuleContext] = useState(null);
  const [, setRefreshKey] = useState(0);

  // Initialize API engines on mount
  useEffect(() => {
    initApiEngines()
      .then(e => { setEngines(e); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Navigate to a module, optionally with context.
  // Modules react to prop/context changes via useEffect — no remount needed.
  const handleNavigate = useCallback((mod, ctx) => {
    setModuleContext(ctx || null);
    setActiveModule(mod);
  }, []);

  // Refresh: reload all caches from API, then remount components
  const refresh = useCallback(async () => {
    if (engines?.reload) {
      await engines.reload();
    }
    setRefreshKey(k => k + 1);
  }, [engines]);

  // Griffin adapter — managed server-side via API
  const [useGriffin, setUseGriffin] = useState(false);
  const [griffinKey, setGriffinKey] = useState('');

  // Check initial Griffin state on mount
  useEffect(() => {
    griffinConfig.get().then(cfg => setUseGriffin(cfg.enabled)).catch(() => {});
  }, []);

  const handleGriffinToggle = useCallback(async (enabled, apiKey) => {
    try {
      const result = await griffinConfig.set(enabled, apiKey || griffinKey);
      setUseGriffin(result.enabled);
    } catch (err) {
      console.error('Griffin toggle failed:', err);
    }
  }, [griffinKey]);

  if (loading) {
    return (
      <div style={{ fontFamily: font, display: 'flex', height: '100vh', background: T.bg, color: T.text, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>CoreBank</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>Connecting to API server...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: font, display: 'flex', height: '100vh', background: T.bg, color: T.text, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: T.red }}>Connection Error</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>{error}</div>
          <div style={{ fontSize: 12, color: T.textSecondary }}>Make sure the API server is running: <code style={{ background: T.surface, padding: '2px 6px', borderRadius: 4 }}>npm run start:api</code></div>
        </div>
      </div>
    );
  }

  const openAlerts = engines.monitoring.getAllOpenAlerts().length;
  const reviewCases = engines.kyc.getAllCases().filter(c => c.status === 'RM_REVIEW').length;
  const pendingApprovals = engines.orchestrator.getPendingApprovals().length;

  return (
    <div style={{ fontFamily: font, display: 'flex', height: '100vh', background: T.bg, color: T.text }}>
      <div style={{ width: 200, background: T.sidebar, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: -0.3 }}>CoreBank</div>
          <div style={{ fontSize: 11, color: T.sidebarText, marginTop: 2 }}>Platform Console</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 8px' }}>
          {NAV_ITEMS.map(item => (
            <button key={item.key} onClick={() => {
              if (item.planned) return;
              handleNavigate(item.key, null);
            }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 6, border: 'none',
              background: activeModule === item.key ? 'rgba(255,255,255,0.1)' : 'transparent',
              color: item.planned ? 'rgba(148,163,184,0.4)' : activeModule === item.key ? T.sidebarActive : T.sidebarText,
              fontSize: 13, fontWeight: activeModule === item.key ? 600 : 400, fontFamily: font,
              cursor: item.planned ? 'default' : 'pointer', textAlign: 'left', marginBottom: 2,
            }}>
              <span style={{ fontSize: 15, opacity: item.planned ? 0.3 : 0.7 }}>{item.icon}</span>
              {item.label}
              {item.key === 'monitoring' && openAlerts > 0 && <span style={{ marginLeft: 'auto', background: T.red, color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{openAlerts}</span>}
              {item.key === 'customers' && reviewCases > 0 && <span style={{ marginLeft: 'auto', background: T.amber, color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{reviewCases}</span>}
              {item.key === 'payments' && pendingApprovals > 0 && <span style={{ marginLeft: 'auto', background: T.amber, color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{pendingApprovals}</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 11, color: T.sidebarText }}>
          <div style={{ fontWeight: 500, color: '#fff' }}>{getUser().name}</div>
          <div style={{ marginBottom: 6 }}>{getUser().username}</div>
          <button onClick={logout} style={{
            background: 'rgba(255,255,255,0.08)', border: 'none', color: T.sidebarText,
            fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontFamily: font,
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{NAV_ITEMS.find(n => n.key === activeModule)?.label}</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
        <div style={{ flex: 1, padding: 24, maxWidth: 1100, margin: '0 auto', width: '100%', overflow: 'auto' }}>
          {activeModule === 'dashboard' && <DashboardModule onNavigate={handleNavigate} />}
          {activeModule === 'crm' && <CRMModule crm={engines.crm} kyc={engines.kyc} ledger={engines.ledger} lending={engines.lending} monitoring={engines.monitoring} compliance={engines.compliance} refresh={refresh} runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile} initialContext={moduleContext} />}
          {activeModule === 'customers' && <KYCModule kyc={engines.kyc} monitoring={engines.monitoring} runQualityGate={runQualityGate} extractActivityProfile={extractActivityProfile} refresh={refresh} />}
          {activeModule === 'accounts' && <AccountsModule ledger={engines.ledger} eventBus={engines.eventBus} eod={engines.eod} refresh={refresh} />}
          {activeModule === 'lending' && <LendingModule lending={engines.lending} customerAccounts={engines.customerAccounts} refresh={refresh} />}
          {activeModule === 'payments' && <PaymentsModule orchestrator={engines.orchestrator} ledger={engines.ledger} eventBus={engines.eventBus} beneficiaries={engines.beneficiaries} standingOrders={engines.standingOrders} refresh={refresh} useGriffin={useGriffin} onGriffinToggle={handleGriffinToggle} griffinKey={griffinKey} setGriffinKey={setGriffinKey} initialContext={moduleContext} />}
          {activeModule === 'monitoring' && <MonitoringModule monitoring={engines.monitoring} kyc={engines.kyc} refresh={refresh} />}
          {activeModule === 'compliance' && <ComplianceModule compliance={engines.compliance} kyc={engines.kyc} monitoring={engines.monitoring} refresh={refresh} />}
          {activeModule === 'invoice-finance' && <InvoiceFinanceModule api={api} refresh={refresh} />}
          {activeModule === 'finance' && <FinanceModule ledger={engines.ledger} refresh={refresh} />}
        </div>
      </div>
    </div>
  );
}
